<script lang="ts" setup>
import { ref } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import {
  CodesCreateDocument,
  KitCreateInput,
  LicenseCreateInput,
  CouponCreateInput,
  CodeType,
  OrganizationType,
} from '@/gql/graphql';
import {
  BAlert,
  BCard,
  BButton,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
} from 'bootstrap-vue-next';
import OrganizationPicker from '@/components/organization/OrganizationPicker.vue';

// --- Component emits ---
const emits = defineEmits(['codes-generated']);

// --- New Codes Generated ---
const newCodes = ref<CodeType[]>([]);

function newCodesGenerated(items: (CodeType | null)[]) {
  newCodes.value = items
    .filter((code): code is CodeType => code !== null)
    .map((code) => ({
      ...code,
      code: code.code,
    }));
  showNewCodesModal.value = true;
}

const showNewCodesModal = ref(false);

// --- Code Generator Parameters ---
const codeLength = ref(4);
const count = ref(1);
const organization = ref<OrganizationType | null>(null);
const orderRef = ref('');
const comment = ref('');

const kitName = ref('CD45+ PBMCs');
const kitDescription = ref('Kit for smart encapsulation of PBMCs (CD45+).');
const kitVersion = ref('');
const kitBarcode = ref('BC1M');
const kitUmiLength = ref(8);
const kitDetails = ref({});
const kitSampleCount = ref(10);
const kitExpirationPeriod = ref(12);
const kitManufacturedDate = ref('');
const generateKit = ref(true);
const generateLicense = ref(false);
const generateCouponStorage = ref(true);
const generateCouponCompute = ref(true);

// --- Mutations State ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

// --- Mutation: Create Codes ---
const {
  mutate: executeCreateCodesMutation,
  onDone: onCreateCodesMutationDone,
  onError: onCreateCodesMutationError,
} = useMutation(CodesCreateDocument);

onCreateCodesMutationDone((res) => {
  mutationInProgress.value = false;
  if (
    res.data &&
    res.data.codesCreate &&
    res.data.codesCreate !== null &&
    res.data.codesCreate.length > 0
  ) {
    console.debug(res.data.codesCreate.length, 'codes generated');
    newCodesGenerated(res.data.codesCreate as (CodeType | null)[]);
    mutationShowSuccess.value = true;
    emits('codes-generated');
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'Codes generation failed: ' + res.data;
    console.error('Codes generation failed:', res.data);
  }
});

onCreateCodesMutationError((err) => {
  console.error('Error generating codes:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function onCodesCreateRequest() {
  console.debug('Codes generation requested');

  // define kit
  let kitCreateInput: KitCreateInput | null = null;

  if (generateKit.value) {
    kitCreateInput = {
      name: kitName.value,
      description: kitDescription.value,
      version: kitVersion.value,
      barcode: kitBarcode.value,
      umiLength: Number(kitUmiLength.value),
      details: kitDetails.value,
      sampleCount: Number(kitSampleCount.value),
      expirationMonths: Number(kitExpirationPeriod.value),
      manufacturedAt: kitManufacturedDate.value
        ? new Date(kitManufacturedDate.value)
        : null,
    };
  }

  // define license
  const licenseCreateInput: LicenseCreateInput | null = null;

  // define coupons
  let couponCreateInputs: CouponCreateInput[] = [];

  // define coupons - storage
  if (generateCouponCompute.value)
    couponCreateInputs.push({
      couponType: 'storage',
      units: 'samples',
      unitsTotal: Number(kitSampleCount.value),
      details: kitDetails.value,
      expiresAt: null,
    });

  // define coupons - compute
  if (generateCouponStorage.value)
    couponCreateInputs.push({
      couponType: 'compute',
      units: 'samples',
      unitsTotal: Number(kitSampleCount.value),
      details: kitDetails.value,
      expiresAt: null,
    });

  // execute mutation
  mutationInProgress.value = true;
  mutationError.value = null;
  executeCreateCodesMutation({
    codeLength: Number(codeLength.value),
    count: Number(count.value),
    organizationId: organization.value?.id || null,
    orderRef: orderRef.value,
    comment: comment.value,
    kit: kitCreateInput,
    license: licenseCreateInput,
    coupons: couponCreateInputs,
  });
}
</script>

<template>
  <div>
    <b-card>
      <template #header>
        <strong
          >Generate new codes with associated kits, licenses and coupons</strong
        >
      </template>
      <div>
        <BForm @submit.prevent="onCodesCreateRequest" size="sm">
          <!-- CODE -->
          <h5 class="mt-1">
            <font-awesome-icon icon="barcode" class="me-2" />
            Basic code configuration
          </h5>
          <b-form-group
            label="Code length"
            label-for="code-length"
            class="mt-3"
          >
            <b-form-input
              id="code-length"
              name="codeLength"
              v-model="codeLength"
              type="number"
              required
            />
          </b-form-group>

          <b-form-group
            label="Number of codes to generate"
            label-for="count"
            class="mt-3"
          >
            <b-form-input
              id="count"
              name="count"
              v-model="count"
              type="number"
              required
            />
          </b-form-group>

          <b-form-group
            label="Order reference"
            label-for="order-ref"
            class="mt-3"
          >
            <b-form-input
              id="order-ref"
              name="orderRef"
              v-model="orderRef"
              placeholder="SH-2025-1284"
              type="text"
            />
          </b-form-group>

          <b-form-group label="Comment" label-for="comment" class="mt-3">
            <b-form-textarea
              id="comment"
              name="comment"
              v-model="comment"
              type="text"
            ></b-form-textarea>
          </b-form-group>

          <!-- ORGANIZATION -->
          <h5 class="mt-4">
            <font-awesome-icon icon="building" class="me-2" />
            Association with an organization
          </h5>
          <OrganizationPicker
            class="mt-3"
            v-model="organization"
            label="Organization"
            :allowNullOrganization="true"
            :includeOwnerFullName="true"
            :includeOwnerEmail="true"
            :includeCountryCode="true"
          />

          <!-- KIT -->
          <h5 class="mt-4">
            <font-awesome-icon icon="cube" class="me-2" />
            Kit
          </h5>

          <BFormCheckbox
            id="generate-kit"
            name="generate-kit"
            v-model="generateKit"
            class="mt-3"
          >
            Create kit
          </BFormCheckbox>

          <b-form-group label="Kit name" label-for="kit-name" class="mt-3">
            <b-form-input
              id="kit-name"
              name="kitName"
              v-model="kitName"
              placeholder="CD45+ PMBCs"
              type="text"
              required
            />
          </b-form-group>

          <b-form-group
            label="Kit description"
            label-for="kit-description"
            class="mt-3"
          >
            <b-form-textarea
              id="kit-description"
              name="kitDescription"
              v-model="kitDescription"
              type="text"
            ></b-form-textarea>
          </b-form-group>

          <b-form-group
            label="Kit version"
            label-for="kit-version"
            class="mt-3"
          >
            <b-form-input
              id="kit-version"
              name="kitVersion"
              v-model="kitVersion"
              placeholder=""
              type="text"
            />
          </b-form-group>

          <b-form-group
            label="Number of samples"
            label-for="kit-sample-count"
            class="mt-3"
          >
            <b-form-input
              id="kit-sample-count"
              name="kitSampleCount"
              v-model="kitSampleCount"
              type="number"
              required
            />
          </b-form-group>

          <b-form-group
            label="Barcodes whitelist"
            label-for="kit-barcode"
            class="mt-3"
          >
            <b-form-input
              id="kit-barcode"
              name="kitBarcode"
              v-model="kitBarcode"
              type="text"
              required
            />
          </b-form-group>

          <b-form-group
            label="UMI length"
            label-for="kit-umi-length"
            class="mt-3"
          >
            <b-form-input
              id="kit-umi-length"
              name="kitUmiLength"
              v-model.number="kitUmiLength"
              type="number"
              required
            />
          </b-form-group>

          <!-- Kit `details` field specs missing: details = GenericScalar() -->

          <b-form-group
            label="Kit expiration period (months)"
            label-for="kit-expiration"
            class="mt-3"
          >
            <b-form-input
              id="kit-expiration"
              name="kitExpiration"
              v-model="kitExpirationPeriod"
              type="number"
              required
            />
          </b-form-group>

          <b-form-group
            label="Kit manufactured date"
            label-for="kit-manufactured-date"
            class="mt-3"
          >
            <b-form-input
              id="kit-manufactured-date"
              name="kitManufacturedDate"
              v-model="kitManufacturedDate"
              type="date"
            ></b-form-input>
          </b-form-group>

          <!-- License (now hidden) -->
          <h5 class="mt-4" hidden>License</h5>
          <!-- Reserved for future -->

          <!-- Coupons -->
          <h5 class="mt-4">
            <font-awesome-icon icon="ticket" class="me-2" />
            Coupons
          </h5>
          <BFormCheckbox
            id="coupon-compute"
            name="coupon-compute"
            v-model="generateCouponStorage"
            class="mt-3"
          >
            Include coupon for storage
          </BFormCheckbox>
          <BFormCheckbox
            id="coupon-storage"
            name="coupon-storage"
            v-model="generateCouponCompute"
            class="mt-3"
          >
            Include coupon for compute
          </BFormCheckbox>

          <!-- Generate Codes Button -->
          <b-button
            class="mt-3"
            variant="primary"
            type="submit"
            :disabled="mutationInProgress"
          >
            <font-awesome-icon :icon="['fas', 'ticket']" class="me-2" />
            <span> Generate Codes </span>
          </b-button>
          <b-alert
            v-model="mutationError"
            variant="danger"
            dismissible
            class="mt-3"
          >
            {{ mutationError }}
          </b-alert>
        </BForm>
      </div>
    </b-card>

    <BModal
      v-model="showNewCodesModal"
      @close="showNewCodesModal = false"
      title="New Codes Generated"
    >
      <p>
        <template v-for="(newCode, idx) in newCodes" :key="idx">
          <span>{{ newCode.code }}</span
          ><br />
        </template>
      </p>
    </BModal>
  </div>
</template>

<style scoped></style>
